import {useCallback, useEffect, useMemo} from "react";
import useSubscription from "./useSubscription";
import {triggerEvent} from "@/utils/custom-events";
import useService from "./useService";
import {fetchAddItem} from "@/services/cart";
import {getCurrentRegion, NON_SUBSCRIPTION_REGIONS} from "@/utils/regions";

const useProductForm = ({
  product,
  defaultVariantId,
  defaultSellingPlanId,
  formId,
}) => {
  const id = defaultVariantId ?? product?.variants[0]?.id;
  const quantity = 1;
  const {
    sellingPlans,
    hasSubscription,
    isActive,
    getSellingPlan,
    sellingPlanId,
    setSellingPlanId,
    setSubscriptionActive,
    isForcedOtp,
  } = useSubscription(product, defaultSellingPlanId);

  // For non-subscription regions, always use OTP pricing (no selling plan)
  const region = getCurrentRegion();
  const shouldForceOTP = region && NON_SUBSCRIPTION_REGIONS.includes(region);
  const effectiveSellingPlanId = shouldForceOTP ? null : sellingPlanId;

  useEffect(() => {
    triggerEvent(`product-form:change:${id}-${formId}`, {
      variantId: id,
      quantity,
      selling_plan: effectiveSellingPlanId,
    });
  }, [id, quantity, effectiveSellingPlanId, formId]);

  const addToCartFn = useCallback(async () => {
    await fetchAddItem({id, quantity, selling_plan: effectiveSellingPlanId});
    triggerEvent("cart:modal:toggle", {
      open: true,
    });
  }, [id, quantity, effectiveSellingPlanId]);

  const addToCartOTPFn = useCallback(async () => {
    await fetchAddItem({id, quantity});
    triggerEvent("cart:modal:toggle", {
      open: true,
    });
  }, [id, quantity]);

  const {loading, serviceCall: addToCart} = useService(addToCartFn);

  const {loading: loadingOTP, serviceCall: addToCartOTP} = useService(addToCartOTPFn);

  const getPrice = useCallback(
    (id, quantity, sellingPlanId) =>
      product.prices.find(
        priceData =>
          priceData.id === id &&
          priceData.quantity === quantity &&
          priceData.selling_plan === sellingPlanId,
      ) ??
      product.prices.find(
        priceData =>
          priceData.id === id && priceData.selling_plan === sellingPlanId,
      ) ??
      product.prices.find(
        priceData => priceData.id === id && priceData.quantity === quantity,
      ) ??
      product.prices.find(priceData => priceData.id === id) ??
      product.prices[0],
    [product],
  );

  const currentPrice = useMemo(
    () => getPrice(id, quantity, effectiveSellingPlanId),
    [getPrice, id, quantity, effectiveSellingPlanId],
  );

  return {
    id,
    quantity,
    getSellingPlan,
    sellingPlanId,
    sellingPlans,
    hasSubscription,
    isActive,
    setSellingPlanId,
    setSubscriptionActive,
    loading,
    loadingOTP,
    addToCartOTP,
    addToCart,
    currentPrice,
    getPrice,
    isForcedOtp,
  };
};

export default useProductForm;
